import { parseISO } from 'date-fns';
import {
  TimelineItemDto,
  MessageTimelineItemDto,
  HistoryTimelineItemDto,
  PagedModelTimelineItemDto,
  UITimelineItem,
  UITimelineData,
  isMessageItem,
  isHistoryItem,
  OrderTimelineDto,
  OrderStatusWithVehicles,
  UserVehicle,
  OrderVehicleAccessLinkDto,
} from '../types/timeline.types';
import { CarHistoryEvent } from '@/components/CarHistoryTimeline/CarHistoryTimeline.types';

/**
 * Mapuje HistoryTimelineItemDto na UITimelineItem
 */
export function mapHistoryItemToUI(
  item: HistoryTimelineItemDto,
): UITimelineItem {
  return {
    id: item.id || '',
    timestamp: item.timestamp ? parseISO(item.timestamp) : new Date(),
    type: 'history',
    title: item.displayTitle || item.title || 'Zdarzenie',
    content: item.displayContent || item.description || '',
    author: item.author
      ? {
          id: item.author.id || '',
          name: item.author.fullName || 'System',
        }
      : undefined,
    isSystemAction: item.isSystemAction || false,
    editable: item.editable || false,
    historyType: item.historyType,
    metadata: item.metadata,
  };
}

/**
 * Mapuje MessageTimelineItemDto na UITimelineItem
 */
export function mapMessageItemToUI(
  item: MessageTimelineItemDto,
): UITimelineItem {
  return {
    id: item.id || '',
    timestamp: item.timestamp ? parseISO(item.timestamp) : new Date(),
    type: 'message',
    title: item.displayTitle || 'Wiadomość',
    content: item.displayContent || item.content || '',
    author: item.author
      ? {
          id: item.author.id || '',
          name: item.author.fullName || 'Nieznany',
        }
      : undefined,
    isSystemAction: item.isSystemAction || false,
    editable: item.editable || false,
    canEdit: item.canEdit,
    edited: item.edited,
  };
}

/**
 * Mapuje pojedynczy TimelineItemDto na UITimelineItem
 * Używa type guards do określenia typu
 */
export function mapTimelineItemToUI(item: TimelineItemDto): UITimelineItem {
  if (isMessageItem(item)) {
    return mapMessageItemToUI(item);
  }

  if (isHistoryItem(item)) {
    return mapHistoryItemToUI(item);
  }

  // Fallback dla nierozpoznanych typów
  return {
    id: item.id || '',
    timestamp: item.timestamp ? parseISO(item.timestamp) : new Date(),
    type: 'history',
    title: item.displayTitle || 'Zdarzenie',
    content: item.displayContent || '',
    author: item.author
      ? {
          id: item.author.id || '',
          name: item.author.fullName || 'System',
        }
      : undefined,
    isSystemAction: item.isSystemAction || false,
    editable: item.editable || false,
  };
}

/**
 * Mapuje PagedModelTimelineItemDto na UITimelineData (z paginacją)
 */
export function mapTimelineResponse(
  response: PagedModelTimelineItemDto,
): UITimelineData {
  // Type assertion: HistoryTimelineItemDto i MessageTimelineItemDto są rozszerzeniami TimelineItemDto
  // Używamy unknown jako pośredniego typu, ponieważ itemType może mieć różne wartości w różnych źródłach typów
  const items = (response.content || []).map((item) =>
    mapTimelineItemToUI(item as unknown as TimelineItemDto),
  );

  const page = response.page || {};
  const totalPages = page.totalPages || 0;
  const currentPage = page.number || 0;

  return {
    items,
    pagination: {
      page: page.number || 0,
      size: page.size || 0,
      totalElements: page.totalElements || 0,
      totalPages: totalPages,
      hasMore: currentPage < totalPages - 1,
    },
  };
}

/**
 * Mapuje HistoryTimelineItemDto na CarHistoryEvent
 * Dla kompatybilności z istniejącym komponentem CarHistoryTimeline
 */
export function mapHistoryItemToCarEvent(
  item: HistoryTimelineItemDto,
): CarHistoryEvent {
  // Mapowanie historyType na CarHistoryEventType
  const mapHistoryTypeToEventType = (
    historyType?: HistoryTimelineItemDto['historyType'],
  ): CarHistoryEvent['type'] => {
    if (!historyType) return 'other';

    const typeMap: Record<string, CarHistoryEvent['type']> = {
      VEHICLE_EVENT: 'other',
      ORDER_EVENT: 'other',
      REPORT_EVENT: 'report_generation',
      EMAIL_SENT: 'other',
      SYSTEM_EVENT: 'other',
    };

    return typeMap[historyType] || 'other';
  };

  return {
    id: item.id || '',
    date: item.timestamp || new Date().toISOString(),
    type: mapHistoryTypeToEventType(item.historyType),
    title: item.displayTitle || item.title || 'Zdarzenie',
    description: item.displayContent || item.description,
    details: item.metadata as CarHistoryEvent['details'],
  };
}

/**
 * Mapuje OrderVehicleAccessLinkDto na UserVehicle
 */
export function mapOrderVehicleToUserVehicle(
  vehicle: OrderVehicleAccessLinkDto,
): UserVehicle {
  return {
    id: vehicle.vehicleId || '',
    brandName: vehicle.vehicleInfo?.brandName,
    modelName: vehicle.vehicleInfo?.modelName,
    maskedVin: vehicle.vehicleInfo?.maskedVin,
    archived: vehicle.archived || false,
    active: vehicle.active || false,
    createdAt: vehicle.createdAt ? parseISO(vehicle.createdAt) : undefined,
    reportAccessLinkId: vehicle.reportAccessLinkId ?? undefined,
    reportAccessLinkStatus: vehicle.reportAccessLinkStatus,
    reportAccessLinkExpiresAt: vehicle.reportAccessLinkExpiresAt
      ? parseISO(vehicle.reportAccessLinkExpiresAt)
      : undefined,
    addons: vehicle.reportAccessLinkAddons || [],
  };
}

/**
 * Mapuje OrderTimelineDto na OrderStatusWithVehicles
 * Konwertuje daty i ekstrahuje pojazdy
 */
export function mapOrderTimelineToStatus(
  orderTimeline: OrderTimelineDto,
): OrderStatusWithVehicles {
  return {
    orderUuid: orderTimeline.orderUuid || '',
    // Używamy externalOrderId jako numeru zamówienia widocznego dla użytkownika
    externalOrderId: orderTimeline.externalOrderId || orderTimeline.orderUuid || '',
    orderTimelineStatus: orderTimeline.orderTimelineStatus,
    orderTimelineStatusDisplayName:
      orderTimeline.orderTimelineStatusDisplayName,
    orderStatus: orderTimeline.orderStatus,
    orderStatusDisplayName: orderTimeline.orderStatusDisplayName,
    brandType: orderTimeline.brandType,
    brandContactEmail: orderTimeline.brandContactEmail,
    brandContactPhone: orderTimeline.brandContactPhone,
    inspectionDate: orderTimeline.inspectionDate
      ? parseISO(orderTimeline.inspectionDate)
      : undefined,
    showPaymentBanner: orderTimeline.showPaymentBanner || false,
    orderSuspensionMessage: orderTimeline.orderSuspensionMessage,
    clientCanChangeVehicle: orderTimeline.clientCanChangeVehicle || false,
    addons: orderTimeline.addons || [],
    vehicles: (orderTimeline.vehicles || []).map(mapOrderVehicleToUserVehicle),
  };
}
